import React, { useEffect, useState } from 'react';

import axios from 'axios';
import _, { cloneDeep, get, set } from 'lodash';
import moment from 'moment';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import {
  array, boolean,
  number,
  object, string,
} from 'yup';

import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faSync } from '@fortawesome/free-solid-svg-icons/faSync';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import Alert from '~/commons/components/Alert.jsx';
import { env } from '~/commons/config';

import Checkbox from '~/commons/components/Checkbox.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormHttpAlert from '~/commons/components/FormHttpAlert.jsx';
import ModalConfirm from '~/commons/components/ModalConfirm.jsx';
import FormResource from './FormResource.jsx';

import { useAsyncResource } from '../../../commons/hooks/useAsyncResource';
import {
  FieldAddresses,
  FieldBasics,
  FieldExtraCards,
  FieldExtraItemsCards,
  FieldPaymentCards,
  FieldPayments,
  FieldProductsList,
  FormConvertBtn,
  // TablePdfRecord,
  // calculate,
  ReportRibbon,
} from './orders';
import FieldConsignPayment from './orders/FieldConsignPayment.jsx';
import {
  arrDefaults,
  countryObj,
  extraDateTimeMap,
  extraSubmit,
  formDefaults,
  removeEmpty,
} from './orders/commons';

const schema = object({
  refCode: string(),
  status: string().required().oneOf(env.status.consignments),
  currency: string()
    .required()
    .oneOf(env.currencies.map((v) => v.code)),
  orgId: number(),
  customer: object({
    erpId: string().nullable(),
  }).nullable(),
  extra: array().of(
    object({
      serviceType: string(),
    }),
  ),
  isTradePrice: boolean(),
});

const defaults = {
  ...formDefaults,
  cartType: 'consignment',
  startAt: '',
  endAt: '',
  consignCustAmt: 0,
  paymentMethod: [arrDefaults.paymentMethod],
  consignPaymentMethod: {
    method: '',
    item: '',
    amount: 0,
  },
};

const FormBody = ({
  values: _values,
  setValues,
  consignment,
  showAlert,
  setShowAlert,
  showModal,
  setShowModal,
  api,
  sales,
  services,
  notFixedQty,
  stored,
  res,
  toOrder,
}) => {
  const {
    resource: values, error, refresh, loading,
  } = useAsyncResource(async () => {
    const { data } = (await axios.post(
      '/api/core/users/self/taxations/calculate',
      { ..._values, _id: get(stored, '_id') },
    )) || {};

    const taxObj = {
      'discount.total': data?.discount ?? 0,
      'taxation.total': data?.tax ?? 0,
      'taxation.calculated': data?.tax ?? 0,
      total: data?.total ?? 0,
      subtotal: data?.subtotal ?? 0,
    };

    const out = cloneDeep(_values);
    Object.entries(taxObj).forEach(([k, v]) => {
      set(out, k, v);
    });

    return out;
  }, [_values]);
  const { resource: products } = useAsyncResource(async () => {
    const currency = _.uniq(_.compact(_.map(_values?.items, (x) => x.item.prices.currency)));
    const exchangeRate = _values.currency ? _.fromPairs(
      await Promise.all(_.map(_.filter(currency, (x) => x !== _values.currency),
        async (x) => [x, (await axios.get(
          '/api/core/users/self/exchange-rates/latest',
          {
            params: {
              from: x,
              to: _values.currency,
            },
          },
        ))?.data?.rate])),
    ) : {};
    return _.map(_values.items, (x) => ({
      ...x,
      item: {
        ...x.item,
        prices: exchangeRate[x.item.prices?.currency] && _values.currency ? {
          currency: _values.currency,
          retailPrice: (Math.round(Number(x.item.prices.retailPrice) * exchangeRate[x.item.prices.currency] * 100) / 100).toFixed(2),
          tradePrice: (Math.round(Number(x.item.prices.tradePrice) * exchangeRate[x.item.prices.currency] * 100) / 100).toFixed(2),
        } : x.item.prices,
      },
    }));
  }, [_values.items, _values.currency]);
  useEffect(() => { if (error) setShowAlert(true); }, [error]);
  return (
    <>
      {consignment?._id && consignment.status !== 'Completed' && (
        consignment?.isTradePrice ? (<>
          <ModalConfirm
            show={showModal}
            onReject={() => setShowModal(false)}
            onConfirm={async () => {
              setShowModal(false);
              toOrder();
            }}
          >
            Verified the customer's identity and confirm to proceed with the trade price
          </ModalConfirm>
          <FormConvertBtn res={res} onClick={() => setShowModal(true)}>
            Convert to Order
          </FormConvertBtn>
          <FormHttpAlert res={res} />
        </>) : (
          <FormConvertBtn res={res} onClick={() => toOrder()}>
            Convert to Order
          </FormConvertBtn>
        )
      )}
      <button
        className="btn btn-outline-primary ml-2 mb-3"
        onClick={(e) => {
          e.nativeEvent.preventDefault();
          refresh();
        }}
        type="button"
        disabled={loading}
      >
        <FA
          icon={loading ? faSpinner : faSync}
          fixedWidth
          pulse={loading}
        />
        Refresh
      </button>
      {(error && showAlert) && (
        <Alert theme="danger" onClose={() => setShowAlert(false)}>
          <span>{error.message}</span>
        </Alert >
      )}
      {consignment && (
        <ReportRibbon api={api} refCode={consignment?.refCode} />
      )}
      <Tabs defaultActiveKey="consignment" unmountOnExit>
        <Tab eventKey="consignment" title="Consignment">
          <FieldBasics
            record={consignment}
            sales={sales}
            status={env?.status?.consignments}
            refCodeName='Cosignment Number'
          />
          <div className="row">
            <div className="col">
              <FormGroup name="startAt" type="date" />
            </div>
            <div className="col">
              <FormGroup name="endAt" type="date" />
            </div>
          </div>
          <FormGroup name="remarks" />
          {consignment?.isTradePrice
            && <>
              <Checkbox value='true' className='checkbox-disabled' />
              <span className="text-muted small my-auto ml-3">
                Verified the customer's identity and confirm to proceed with the trade price
              </span>
            </>
          }
          <FieldAddresses formData={values} />
        </Tab>
        <Tab eventKey="extra" title="Extra Services">
          <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Extra</h6>
          <FieldExtraCards services={services} notFixedQty={notFixedQty} />
        </Tab>
        <Tab eventKey="payment" title="Payment">
          <FieldPayments order={consignment} values={values} />
          <FieldConsignPayment values={_values} products={products} setValues={setValues} />
          <FieldPaymentCards />
        </Tab>
        <Tab eventKey="products" title="Products list">
          <div className="py-3">
            <FieldProductsList consignment={true} order={consignment} />
          </div>
        </Tab>
        <Tab eventKey="extraItems" title="Custom Item">
          <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Custom Item</h6>
          <FieldExtraItemsCards />
        </Tab>
        {/* <Tab eventKey="pdfRecord" title="PDF Record">
              <TablePdfRecord
                target={consignment?._id}
                type="consignment"
                url={`${api}/pdfRecord`}
              />
            </Tab> */}
      </Tabs>
    </>
  );
};

const FormConsignment = ({
  consignment, services, sales = [], toOrder = () => { }, res,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  const api = '/api/core/consignments';

  const notFixedQty = services
    .filter(({ fixedQty = true }) => !fixedQty)
    .reduce((a, v) => ({ ...a, [v._id]: v }), {});

  const stored = consignment
    ? (() => {
      const extra = extraDateTimeMap(consignment.extra);
      const startAt = consignment.startAt
        ? moment(consignment.startAt).format('YYYY-MM-DD')
        : '';
      const endAt = consignment.endAt ? moment(consignment.endAt).format('YYYY-MM-DD') : '';
      return {
        ...consignment,
        extra,
        startAt,
        endAt,
      };
    })()
    : undefined;

  return (
    <FormResource
      {...{
        defaults,
        schema,
        initialValuesOpts: {
          omitIds: false,
          transform: v => ({
            ...v,
            extra: _.map(v.extra, x => ({ ...x, service: x.service?._id })),
          }),
        },
        stored,
        api,
        onSubmit: (v) => {
          let data = cloneDeep(v);

          data = removeEmpty(data, ['extra', 'paymentMethod', 'extraItems']);
          data.extra = extraSubmit(data.extra);

          if (consignment) {
            delete data.customer;
          }
          if (!data.issuedBy) {
            data.issuedBy = null;
          }

          ['billingAddress', 'shippingAddress'].forEach((x) => {
            data[x].country = countryObj[data[x].countryCode] || '';
          });

          data.startAt = data.startAt ? moment(data.startAt).startOf('days').toDate() : null;
          data.endAt = data.endAt ? moment(data.endAt).startOf('days').toDate() : null;

          data.orgId = parseInt(data.orgId, 10) || undefined;
          data.items = data.items.map((x) => ({ ...x, item: x.item?._id })).filter((x) => !!x.item);

          return data;
        },
      }}>
      {({ values, setValues }) => <FormBody
        values={values}
        setValues={setValues}
        consignment={consignment}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        showModal={showModal}
        setShowModal={setShowModal}
        api={api}
        sales={sales}
        services={services}
        notFixedQty={notFixedQty}
        stored={stored}
        res={res}
        toOrder={toOrder}
      />}
    </FormResource>
  );
};

export default FormConsignment;
